import React from 'react';

import {
  ChipField,
  Datagrid,
  DateField,
  FunctionField,
  Loading,
  RecordContextProvider,
  ReferenceField,
  SingleFieldList,
  useGetOne,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, {
  AuditTrack,
  ExtendedAuditTrackDiff,
} from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import SelectDiffField from '../utils/auditTracks/SelectDiffField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import ReferenceDiffField from '../utils/auditTracks/ReferenceDiffField';
import ReferenceArrayDiffField from '../utils/auditTracks/ReferenceArrayDiffField';
import ReferenceEntityDiffField from '../utils/auditTracks/ReferenceEntityDiffField';
import { ICarPurchase, IRequest, ITransaction } from './types';
import requestTypesChoices, { requestTypes } from '../../constants/requestTypes';
import { transmodeTypesChoices } from '../../constants/transmodeTypes';
import { currencyChoices } from '../../constants/currencies';
import resourceNames from '../../constants/resourceNames';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import { CommentMessageField } from '../comment/CommentList';
import hasAccess from '../../utilities/hasAccess';
import PermissionMissing from '../utils/PermissionMissing';
import actions, { Actions } from '../../constants/actions';

type RequestAuditResource = ITransaction | ICarPurchase | IRequest;
type RequestAuditTrack = AuditTrack<RequestAuditResource>;
type RequestAuditResourceData = ExtendedAuditTrackDiff<RequestAuditResource>['resourceData'];
type TransactionExtraInformationDiff = ExtendedAuditTrackDiff<ITransaction>['resourceData']['extraInformation'];
type CarPurchaseExtraInformationDiff = ExtendedAuditTrackDiff<ICarPurchase>['resourceData']['extraInformation'];

const RequestAuditDetails = () => {
  const record = useRecordContext<RequestAuditTrack>();
  const { data, isLoading } = useGetOne<RequestAuditTrack>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );
  const { permissions } = usePermissions<Actions[]>();

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<RequestAuditResource>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;
  const typedResourceData = resourceData as RequestAuditResourceData;
  const transactionExtraInformation = typedResourceData.extraInformation as
    TransactionExtraInformationDiff | undefined;
  const carPurchaseExtraInformation = typedResourceData.extraInformation as
    CarPurchaseExtraInformationDiff | undefined;
  const { requestType } = record.documentAfter;

  const hasFieldChanges: Record<string, boolean | undefined> = {
    id: typedResourceData.id?.hasChanged,
    customId: typedResourceData.customId?.hasChanged,
    requestType: typedResourceData.requestType?.hasChanged,
    step: typedResourceData.step?.hasChanged,
    recommendation: typedResourceData.recommendation?.hasChanged,
    processStopped: typedResourceData.processStopped?.hasChanged,
    process: typedResourceData.process?.hasChanged,
    signals: typedResourceData.signals?.hasChanged,
    comments: typedResourceData.comments?.hasChanged,
    transactionAmount: transactionExtraInformation?.amount?.hasChanged,
    transactionCurrency: transactionExtraInformation?.currency?.hasChanged,
    transactionDate: transactionExtraInformation?.date?.hasChanged,
    transactionReference: transactionExtraInformation?.reference?.hasChanged,
    transactionTextMessage: transactionExtraInformation?.textMessage?.hasChanged,
    transactionChannel: transactionExtraInformation?.transactionChannel?.hasChanged,
    transactionChannelProvider:
      transactionExtraInformation?.transactionChannelProvider?.hasChanged,
    transactionInitiatorId: transactionExtraInformation?.initiator?.id?.hasChanged,
    transactionInitiatorType: transactionExtraInformation?.initiator?.type?.hasChanged,
    transactionRecipientId: transactionExtraInformation?.recipient?.id?.hasChanged,
    transactionRecipientType: transactionExtraInformation?.recipient?.type?.hasChanged,
    transactionProcessorId: transactionExtraInformation?.processor?.id?.hasChanged,
    transactionProcessorType: transactionExtraInformation?.processor?.type?.hasChanged,
    transactionSubProcessorId: transactionExtraInformation?.subProcessor?.id?.hasChanged,
    transactionSubProcessorType: transactionExtraInformation?.subProcessor?.type?.hasChanged,
    carPurchaseAmount: carPurchaseExtraInformation?.amount?.hasChanged,
    carPurchaseCurrency: carPurchaseExtraInformation?.currency?.hasChanged,
    carPurchaseDate: carPurchaseExtraInformation?.date?.hasChanged,
    carPurchaseReference: carPurchaseExtraInformation?.reference?.hasChanged,
    carPurchaseTextMessage: carPurchaseExtraInformation?.textMessage?.hasChanged,
    carPurchaseChannel: carPurchaseExtraInformation?.transactionChannel?.hasChanged,
    carPurchaseChannelProvider:
      carPurchaseExtraInformation?.transactionChannelProvider?.hasChanged,
    carPurchaseSellerId: carPurchaseExtraInformation?.seller?.id?.hasChanged,
    carPurchaseSellerType: carPurchaseExtraInformation?.seller?.type?.hasChanged,
    carPurchaseBuyerId: carPurchaseExtraInformation?.buyer?.id?.hasChanged,
    carPurchaseBuyerType: carPurchaseExtraInformation?.buyer?.type?.hasChanged,
    carPurchaseCarId: carPurchaseExtraInformation?.car?.id?.hasChanged,
    carPurchaseCarType: carPurchaseExtraInformation?.car?.type?.hasChanged,
    carPurchaseGuarantorId: carPurchaseExtraInformation?.guarantor?.id?.hasChanged,
    carPurchaseGuarantorType: carPurchaseExtraInformation?.guarantor?.type?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.id && <TextDiffField source="id" label="ID" />}

          {hasFieldChanges.customId && <TextDiffField source="customId" label="Custom ID" />}

          {hasFieldChanges.requestType && (
            <SelectDiffField
              source="requestType"
              label="Request type"
              choices={Object.values(requestTypesChoices)}
            />
          )}

          {hasFieldChanges.step && <TextDiffField source="step" label="Step" />}

          {hasFieldChanges.recommendation && (
            <StatusDiffField source="recommendation" label="Recommendation" />
          )}

          {hasFieldChanges.processStopped && (
            <TextDiffField source="processStopped" label="Process stopped" />
          )}

          {hasFieldChanges.process && (
            <ReferenceDiffField
              source="process"
              label="Process"
              reference={resourceNames.PROCESSES}
              hasLink={false}
            />
          )}

          {hasFieldChanges.signals && (
            <ReferenceArrayDiffField
              source="signals"
              label="Signals"
              reference={resourceNames.SIGNALS}
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" size="small" />
              </SingleFieldList>
            </ReferenceArrayDiffField>
          )}

          {hasFieldChanges.comments && (
            <ArrayDiffField source="comments" label="Comments" width="100%" size={{ xs: 12 }}>
              <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
                <CommentMessageField emptyText="-" label="Comment" />
                <DateField showTime source="date" />
                {hasAccess(permissions, actions.MEMBER_GET) ? (
                  <ReferenceField label="Author" source="member" reference={resourceNames.MEMBERS} link="edit">
                    <FunctionField render={(member: { firstName: string; lastName: string }) => `${member.firstName} ${member.lastName}`} />
                  </ReferenceField>
                ) : (<PermissionMissing />)}
              </Datagrid>
            </ArrayDiffField>
          )}
          {requestType === requestTypes.TRANSACTION && (
            <>
              {hasFieldChanges.transactionAmount && (
                <TextDiffField source="extraInformation.amount" label="Amount" />
              )}
              {hasFieldChanges.transactionCurrency && (
                <SelectDiffField
                  source="extraInformation.currency"
                  label="Currency"
                  choices={currencyChoices}
                />
              )}
              {hasFieldChanges.transactionDate && (
                <DateDiffField source="extraInformation.date" label="Transaction date" showTime />
              )}
              {hasFieldChanges.transactionReference && (
                <TextDiffField source="extraInformation.reference" label="Reference" />
              )}
              {hasFieldChanges.transactionTextMessage && (
                <TextDiffField source="extraInformation.textMessage" label="Text message" />
              )}
              {hasFieldChanges.transactionChannel && (
                <SelectDiffField
                  source="extraInformation.transactionChannel"
                  label="Transaction channel"
                  choices={transmodeTypesChoices}
                />
              )}
              {hasFieldChanges.transactionChannelProvider && (
                <TextDiffField
                  source="extraInformation.transactionChannelProvider"
                  label="Transaction channel provider"
                />
              )}

              {(hasFieldChanges.transactionInitiatorId
                || hasFieldChanges.transactionInitiatorType) && (
                <ReferenceEntityDiffField source="extraInformation.initiator" label="Initiator" />
              )}
              {(hasFieldChanges.transactionRecipientId
                || hasFieldChanges.transactionRecipientType) && (
                <ReferenceEntityDiffField source="extraInformation.recipient" label="Recipient" />
              )}
              {(hasFieldChanges.transactionProcessorId
                || hasFieldChanges.transactionProcessorType) && (
                <ReferenceEntityDiffField source="extraInformation.processor" label="Processor" />
              )}
              {(hasFieldChanges.transactionSubProcessorId
                || hasFieldChanges.transactionSubProcessorType) && (
                <ReferenceEntityDiffField source="extraInformation.subProcessor" label="Sub-processor" />
              )}
            </>
          )}

          {requestType === requestTypes.CAR_PURCHASE && (
            <>
              {hasFieldChanges.carPurchaseAmount && (
                <TextDiffField source="extraInformation.amount" label="Amount" />
              )}
              {hasFieldChanges.carPurchaseCurrency && (
                <SelectDiffField
                  source="extraInformation.currency"
                  label="Currency"
                  choices={currencyChoices}
                />
              )}
              {hasFieldChanges.carPurchaseDate && (
                <DateDiffField source="extraInformation.date" label="Purchase date" showTime />
              )}
              {hasFieldChanges.carPurchaseReference && (
                <TextDiffField source="extraInformation.reference" label="Reference" />
              )}
              {hasFieldChanges.carPurchaseTextMessage && (
                <TextDiffField source="extraInformation.textMessage" label="Text message" />
              )}
              {hasFieldChanges.carPurchaseChannel && (
                <TextDiffField source="extraInformation.transactionChannel" label="Transaction channel" />
              )}
              {hasFieldChanges.carPurchaseChannelProvider && (
                <TextDiffField
                  source="extraInformation.transactionChannelProvider"
                  label="Transaction channel provider"
                />
              )}

              {(hasFieldChanges.carPurchaseSellerId
                || hasFieldChanges.carPurchaseSellerType) && (
                <ReferenceEntityDiffField source="extraInformation.seller" label="Seller" />
              )}
              {(hasFieldChanges.carPurchaseBuyerId
                || hasFieldChanges.carPurchaseBuyerType) && (
                <ReferenceEntityDiffField source="extraInformation.buyer" label="Buyer" />
              )}
              {(hasFieldChanges.carPurchaseCarId
                || hasFieldChanges.carPurchaseCarType) && (
                <ReferenceEntityDiffField source="extraInformation.car" label="Car" />
              )}
              {(hasFieldChanges.carPurchaseGuarantorId
                || hasFieldChanges.carPurchaseGuarantorType) && (
                <ReferenceEntityDiffField source="extraInformation.guarantor" label="Guarantor" />
              )}
            </>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default RequestAuditDetails;
